import React, { useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { toastr } from 'react-redux-toastr';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Grid, Typography } from '@material-ui/core';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';

import TituloPagina from '../../components/layout/TituloPagina';
import FileList from '../../components/templates/fileList/FileList';

import {
    carregarUploadedFiles,
    addImagemProduto,
    getProduto,
    getImagensProduto,
    deleteImagemProduto,
    salvarOrdemImagens
} from '../../store/actions/produto';

import {
    Container,
    Content,
    DropContainer,
    UploadMessage
} from './styles';

const MAX_SIZE = 5000000; // 5.000kb (5Mb)
const MAX_FILES = 5;
const MIN_WIDTH = 800;
const MIN_HEIGHT = 1000;
const RECOMMENDED_RATIO = 4 / 5;
const RATIO_TOLERANCE = 0.08;

const hasValidImageId = (file) => {
    const imageId = Number(file && file.id);
    return Number.isFinite(imageId) && imageId > 0;
};

const normalizePrincipalAndOrder = (files = []) => {
    return files.map((file, index) => ({
        ...file,
        principal: index === 0,
        ordem: index + 1,
    }));
};

const UploadImagensProduto = props => {
    const history = useHistory();
    const location = useLocation();
    const id = Number(location?.state?.produtoId);
    const isEditRoute = location.pathname === '/produtos/imagens-do-produto';
    const {
        getProduto,
        produto,
        uploadedFiles,
        existingFiles,
        carregarUploadedFiles,
        addImagemProduto,
        salvarOrdemImagens,
        deleteImagemProduto,
        getImagensProduto
    } = props;

    useEffect(() => {

        if (isEditRoute && !id) {
            history.replace('/produtos');
            return;
        }

        if (id > 0) {
            getProduto(id);
            getImagensProduto(id);
        }
    }, [getProduto, getImagensProduto, history, location]);

    // Hook para sincronizar imagens do banco com a lista de exibição
    useEffect(() => {
        if (existingFiles) {
            const savedFiles = existingFiles
                .sort((a, b) => a.ordem - b.ordem)
                .map(file => ({
                    id: file.id,
                    idTemp: `db_${file.id}`,
                    readableSize: null,
                    preview: file.url,
                    progress: 100,
                    uploaded: true,
                    error: false,
                    url: file.url,
                    principal: file.principal,
                    idProduto: file.idProduto,
                    ordem: file.ordem
                }));

            // Preserva itens locais em upload/erro para não perder o vínculo de idTemp
            const localTransientFiles = uploadedFiles.filter((file) => {
                const transientId = String(file.idTemp || '');
                const isInFlightUpload = !file.uploaded && !file.error;
                return isInFlightUpload && !hasValidImageId(file) && !transientId.startsWith('db_');
            });

            carregarUploadedFiles([...savedFiles, ...localTransientFiles]);
        }

        // Cleanup: Remove URLs de preview da memória ao desmontar o componente
        return () => {
            uploadedFiles.forEach(file => {
                if (file.preview && file.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, [existingFiles, carregarUploadedFiles]);

    const handleUpload = async (files) => {
        const currentCount = uploadedFiles.length;

        if (currentCount >= MAX_FILES) {
            toastr.error('Erro', 'Limite de 5 imagens atingido.');
            return;
        }

        const filesToProcess = files.slice(0, MAX_FILES - currentCount);

        if (files.length > (MAX_FILES - currentCount)) {
            toastr.warning('Aviso', 'Alguns arquivos foram ignorados (limite de 5).');
        }

        const jaTemPrincipal = uploadedFiles.some((imagem) => imagem.principal);
        const maiorOrdemAtual = uploadedFiles.reduce((maior, imagem) => {
            const ordemAtual = Number(imagem.ordem || 0);
            return ordemAtual > maior ? ordemAtual : maior;
        }, 0);

        const resultados = await Promise.all(
            filesToProcess.map((file, index) => new Promise((resolve) => {
                const img = new Image();
                const previewUrl = URL.createObjectURL(file);

                img.onload = () => {
                    const width = img.width;
                    const height = img.height;
                    const ratio = width / height;
                    const ratioDiff = Math.abs(ratio - RECOMMENDED_RATIO);

                    if (width < MIN_WIDTH || height < MIN_HEIGHT) {
                        toastr.warning(
                            'Qualidade Baixa',
                            `${file.name} tem resolução menor que a recomendada (${MIN_WIDTH}x${MIN_HEIGHT}).`
                        );
                    }

                    if (ratioDiff > RATIO_TOLERANCE) {
                        toastr.info(
                            'Proporção fora do padrão',
                            `${file.name} está fora do formato recomendado 4:5 e pode sofrer recorte na vitrine.`
                        );
                    }

                    const principal = !jaTemPrincipal && currentCount === 0 && index === 0;
                    const ordem = maiorOrdemAtual + index + 1;

                    const fileObj = {
                        file,
                        idTemp: uniqueId('upload_'),
                        id: 0,
                        readableSize: filesize(file.size),
                        preview: previewUrl,
                        progress: 0,
                        uploaded: false,
                        error: false,
                        url: null,
                        idProduto: produto.id,
                        principal,
                        ordem,
                        width,
                        height,
                        ratio
                    };

                    resolve(fileObj);
                };

                img.onerror = () => {
                    URL.revokeObjectURL(previewUrl);
                    toastr.error('Imagem inválida', `${file.name} não pôde ser processada.`);
                    resolve(null);
                };

                img.src = previewUrl;
            }))
        );

        const novosArquivos = resultados.filter(Boolean);
        if (novosArquivos.length === 0) return;

        carregarUploadedFiles([...uploadedFiles, ...novosArquivos]);
        novosArquivos.forEach((fileObj) => addImagemProduto(fileObj));
    };

    const isUploading = uploadedFiles.some(f => !f.uploaded && !f.error);
    const hasAtLeastOneImage = uploadedFiles.length > 0;
    const hasPrincipalImage = uploadedFiles.length > 0 && uploadedFiles.some((f, index) => f.principal || index === 0);
    const lowQualityImages = uploadedFiles.filter(
        (f) => typeof f.width === 'number' && typeof f.height === 'number' && (f.width < MIN_WIDTH || f.height < MIN_HEIGHT)
    );
    const hasLowQualityImages = lowQualityImages.length > 0;

    const canFinalize = !isUploading && hasAtLeastOneImage && hasPrincipalImage && !hasLowQualityImages;

    const validationMessages = [];
    if (!hasAtLeastOneImage) {
        validationMessages.push('Adicione pelo menos 1 imagem para o produto.');
    }
    if (hasAtLeastOneImage && !hasPrincipalImage) {
        validationMessages.push('Defina uma imagem como principal para melhorar a vitrine.');
    }
    if (hasLowQualityImages) {
        validationMessages.push(
            `${lowQualityImages.length} imagem(ns) com resolução abaixo do mínimo (${MIN_WIDTH}x${MIN_HEIGHT}).`
        );
    }

    const handleReorder = (newOrder) => {
        const normalizedOrder = normalizePrincipalAndOrder(newOrder);

        // 1. Atualiza o estado local (Redux Store) para a lista não "pular" de volta
        carregarUploadedFiles(normalizedOrder);

        // 2. Filtra apenas imagens que já possuem ID (já foram salvas no banco)
        const imagensSalvas = normalizedOrder.filter((img) => hasValidImageId(img));

        // 3. Salva a nova ordem no servidor
        if (imagensSalvas.length > 0) {
            salvarOrdemImagens(id, imagensSalvas);
        }
    };

    const handleDeleteFile = (fileId, uploadedFile) => {
        if (!uploadedFile) return;

        const isPersisted = hasValidImageId(uploadedFile) && !uploadedFile.error;

        if (isPersisted) {
            deleteImagemProduto(fileId);
            return;
        }

        if (uploadedFile.preview && uploadedFile.preview.startsWith('blob:')) {
            URL.revokeObjectURL(uploadedFile.preview);
        }

        const filesWithoutRejected = uploadedFiles.filter(
            (file) => (file.idTemp || String(file.id)) !== (uploadedFile.idTemp || String(uploadedFile.id))
        );

        carregarUploadedFiles(normalizePrincipalAndOrder(filesWithoutRejected));
        toastr.info('Imagem removida', 'Imagem rejeitada removida da lista.');
    };

    return (
        <>
            <TituloPagina titulo={`Imagens para ${produto.nome || ''}`} />

            <Grid container item xs={12} style={{ padding: '15px 0' }}>
                <Typography variant="body1">
                    As imagens devem possuir extenção PNG ou JPG e possuir no máximo 5 Megabytes. É recomendável 
                    o uso de imagens com resolução mínima de {MIN_WIDTH}px x {MIN_HEIGHT}px. Limite de 5 imagens por produto.
                </Typography>
            </Grid>

            <Container>
                <Content>
                    <Dropzone
                        accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
                        onDrop={handleUpload}
                        maxSize={MAX_SIZE}
                    >
                        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                            <DropContainer
                                {...getRootProps()}
                                $isDragActive={isDragActive}
                                $isDragReject={isDragReject}
                            >
                                <input {...getInputProps()} />
                                {!isDragActive && <UploadMessage>Clique ou arraste imagens aqui.</UploadMessage>}
                                {isDragActive && !isDragReject && <UploadMessage type="success">Solte para enviar!</UploadMessage>}
                                {isDragReject && <UploadMessage type="error">Arquivo não suportado ou muito grande.</UploadMessage>}
                            </DropContainer>
                        )}
                    </Dropzone>

                    {uploadedFiles.length > 0 && (
                        <FileList
                            files={uploadedFiles}
                            onDelete={handleDeleteFile}
                            onReorder={handleReorder} // Passe a nova função aqui
                        />
                    )}
                </Content>
            </Container>

            <Grid container style={{ marginTop: '20px' }}>
                {validationMessages.length > 0 && (
                    <Grid item xs={12} style={{ marginBottom: '12px' }}>
                        <Typography variant="body2" color="error">
                            {validationMessages.join(' ')}
                        </Typography>
                    </Grid>
                )}

                <Button
                    variant='contained'
                    color='primary'
                    disabled={!canFinalize}
                    href='/produtos'
                >
                    {isUploading ? 'Enviando...' : 'Finalizar'}
                </Button>
            </Grid>
        </>
    );
};

const mapStateToProps = state => ({
    produto: state.produto.produto,
    uploadedFiles: state.produto.uploadedFiles,
    imagens: state.produto.imagens,
    existingFiles: state.produto.existingFiles
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getProduto,
            getImagensProduto,
            carregarUploadedFiles,
            addImagemProduto,
            salvarOrdemImagens,
            deleteImagemProduto
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(UploadImagensProduto);